import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { postData } from './apiService';
import { jwtDecode } from 'jwt-decode';

const SessionTimeout = () => {
    const { login, ClearToken, token, logout, companyName, branchId } = useAuth();
    const [showWarning, setShowWarning] = useState(false);

    //const sessionTimeoutDuration = 1200000; // 20 minutes
    //const warningTime = 300000; // 5 minutes in milliseconds
    const sessionTimeoutDuration = 3600000; // 1 hour in milliseconds
    const warningTime = 1800000; // 30 minutes in milliseconds


    let warningTimer;
    let logoutTimer;

    const resetTimers = () => {
        clearTimeout(warningTimer);
        clearTimeout(logoutTimer);
    };

    const handleActivity = useCallback(() => {
        resetTimers();
        if (token !== null && token !== undefined) {
            const decodedToken = jwtDecode(token);
            const currentTimestamp = Date.now() / 1000;
            if (decodedToken.exp < currentTimestamp) {
                logout();
                return;
            }
        }


        warningTimer = setTimeout(() => {
            setShowWarning(true);

            logoutTimer = setTimeout(() => {
                setShowWarning(false); // Hide the warning after logout timer expires
                logout();
            }, 60000); // Logout after 2 min of warning
        }, sessionTimeoutDuration - warningTime);
    }, [logout]);

    useEffect(() => {
        handleActivity();

        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keydown', handleActivity);

        return () => {
            resetTimers();
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keydown', handleActivity);
        };

    }, [handleActivity]);

    const handleRefresh = async () => {
        try {
            const jwtToken = await performLogin();
            
            if (!jwtToken) {
                // Token was expired, logout already handled
                return;
            }
            
            const responseData = jwtDecode(jwtToken);
            const keysToRemove = ['iss', 'exp', 'ttp', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name', 'aud'];
            const permissions = Object.keys(responseData).filter(
                (key) => key !== 'companyId' && key !== 'userId' && !keysToRemove.includes(key)
            );

            let isSuper = false;
            if (responseData.isSuperAdmin.toLowerCase() === "true") {
                isSuper = true;
            }
            let compname = companyName;
            ClearToken();

            login(responseData.companyId, responseData.userId, isSuper, responseData.superAdminCompanyId, compname, permissions, jwtToken);
            setShowWarning(false); // Set showWarning to false after successful refresh
            handleActivity();
        } catch (error) {
            console.error('Error refreshing session:', error);
            // On error, logout the user
            handleLogout();
        }
    };

    const performLogin = async () => {
        try {
            const decodedToken = jwtDecode(token);

            const currentTimestamp = Date.now() / 1000;
            if (decodedToken.exp < currentTimestamp) {
                // Token has expired, force logout
                resetTimers();
                setShowWarning(false);
                logout();
                return null;
            }

            const companyId = decodedToken.companyId;
            const userId = decodedToken.userId;
            const username = decodedToken.username;

            const loginData = {
                companyId: companyId,
                branchId: branchId,
                userId: userId,
                username: username
            };

            const response = await postData('UserTokenApi', loginData, token);

            if (response.responseCode === '1111') {
                const jwtToken = response.response_data.token;
                return jwtToken;
            } else {
                throw new Error(response.Message);
            }
        } catch (error) {
            console.error('Error in performLogin:', error);
            throw error;
        }
    };

    const handleLogout = () => {
        resetTimers();
        setShowWarning(false); // Set showWarning to false before logging out
        logout();
    };

    const styles = {
        warningContainer: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 0, 0, 0.2)', // Semi-transparent red background
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999,
        },
        warningBox: {
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
        },
        button: {
            margin: '0 10px',
            padding: '8px 16px',
            fontSize: '14px',
            cursor: 'pointer',
            borderRadius: '4px',
            border: '1px solid #ccc',
        },
        refreshButton: {
            background: 'green',
            color: 'white',
        },
        logoutButton: {
            background: 'red',
            color: 'white',
        },
    };


    return (
        <div>
            {showWarning && (
                <div style={styles.warningContainer}>
                    <div style={styles.warningBox}>
                        <p>Your session is about to expire. Do you want to refresh?</p>
                        <button style={{ ...styles.button, ...styles.refreshButton }} onClick={handleRefresh}>
                            Refresh
                        </button>
                        <button style={{ ...styles.button, ...styles.logoutButton }} onClick={handleLogout}>
                            Logout
                        </button>

                    </div>
                </div>
            )}
        </div>
    );
};

export default SessionTimeout;
